// library.ts — the named Szenario-Bibliothek (Feature 3): save the current scenario
// under a name, list/rename/delete saved entries, load one into slot A or B. Separate
// localStorage key from the A/B slots (storage.ts) since this is an open-ended list
// rather than a fixed two-slot shape — v1, brand new key, nothing to migrate from yet.
// Pure list-transform functions (addToLibrary/renameInLibrary/removeFromLibrary) are
// separated from the load/save I/O wrappers, same split as scenario.ts (pure) vs.
// storage.ts (I/O), so the transforms are trivially unit-testable without touching
// localStorage.
import type { ScenarioState } from './scenario.js';

const LIBRARY_KEY = 'etf-cockpit:library:v1';
/** Sane cap so a runaway "save" loop can't silently fill localStorage; keeps the most
 * recently added entries when exceeded. */
const MAX_ENTRIES = 100;

export interface LibraryEntry {
  id: string;
  name: string;
  /** ISO timestamp of when this entry was saved (informational, shown in the list). */
  savedAt: string;
  scenario: ScenarioState;
}

function newId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `entry-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isLibraryEntry(x: unknown): x is LibraryEntry {
  if (typeof x !== 'object' || x === null) return false;
  const e = x as Record<string, unknown>;
  return (
    typeof e['id'] === 'string' &&
    typeof e['name'] === 'string' &&
    typeof e['savedAt'] === 'string' &&
    typeof e['scenario'] === 'object' &&
    e['scenario'] !== null
  );
}

/** Appends a new entry for `scenario` under `name`, capped at MAX_ENTRIES (drops the
 * OLDEST entries first — the most recently saved scenarios are the ones worth keeping). */
export function addToLibrary(entries: LibraryEntry[], name: string, scenario: ScenarioState): LibraryEntry[] {
  const entry: LibraryEntry = {
    id: newId(),
    name: name.trim() || 'Unbenanntes Szenario',
    savedAt: new Date().toISOString(),
    scenario,
  };
  const next = [...entries, entry];
  return next.length > MAX_ENTRIES ? next.slice(next.length - MAX_ENTRIES) : next;
}

export function renameInLibrary(entries: LibraryEntry[], id: string, name: string): LibraryEntry[] {
  const trimmed = name.trim();
  if (trimmed === '') return entries;
  return entries.map((e) => (e.id === id ? { ...e, name: trimmed } : e));
}

export function removeFromLibrary(entries: LibraryEntry[], id: string): LibraryEntry[] {
  return entries.filter((e) => e.id !== id);
}

export function findInLibrary(entries: LibraryEntry[], id: string): LibraryEntry | undefined {
  return entries.find((e) => e.id === id);
}

export function loadLibrary(): LibraryEntry[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LIBRARY_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isLibraryEntry);
  } catch {
    return [];
  }
}

export function saveLibrary(entries: LibraryEntry[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LIBRARY_KEY, JSON.stringify(entries));
  } catch {
    // Storage full / disabled (private browsing) — silently drop, matching storage.ts.
  }
}
